export interface RawBlock {
  /** e.g. "Table", "Enum", "Ref", "TableGroup", "Project" */
  keyword: string;
  /** everything between the keyword and the opening `{` (or, for single-line Refs, the rest of the line) */
  header: string;
  /** lines inside the braces, or empty for single-line statements */
  bodyLines: string[];
  /** 1-indexed source line the block starts on */
  startLine: number;
  hasBraces: boolean;
}

const BLOCK_KEYWORDS = ['Project', 'Table', 'Enum', 'TableGroup', 'Ref', 'TablePartial', 'Note'];

/** Strips a trailing `// comment`, respecting single/double/backtick quoted strings. */
function stripLineComment(line: string): string {
  let inQuote: string | null = null;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuote) {
      if (ch === inQuote) inQuote = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      inQuote = ch;
      continue;
    }
    if (ch === '/' && line[i + 1] === '/') {
      return line.slice(0, i);
    }
  }
  return line;
}

/**
 * Splits DBML source into top-level blocks. Assumes DBML's actual grammar: constructs
 * (Table/Enum/TableGroup/Project/Ref-with-body) are brace-delimited and never nest another
 * top-level construct, plus single-line `Ref: a.b > c.d` statements with no braces.
 */
export function splitTopLevelBlocks(source: string): RawBlock[] {
  const rawLines = source.split(/\r?\n/).map(stripLineComment);
  const blocks: RawBlock[] = [];

  let i = 0;
  while (i < rawLines.length) {
    const line = rawLines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      i += 1;
      continue;
    }

    const keywordMatch = trimmed.match(/^(\w+)\b/);
    const keyword = keywordMatch ? keywordMatch[1] : '';

    if (!BLOCK_KEYWORDS.includes(keyword)) {
      // Unrecognized top-level line; skip it (surfaced by the parser as a warning if needed).
      i += 1;
      continue;
    }

    const startLine = i + 1;

    if (keyword === 'Ref' && !trimmed.includes('{')) {
      // Single-line form: `Ref: table.col > table2.col2`
      const header = trimmed.replace(/^Ref\s*:?/, '').trim();
      blocks.push({ keyword, header, bodyLines: [], startLine, hasBraces: false });
      i += 1;
      continue;
    }

    // Brace-delimited block: accumulate until braces balance back to zero.
    let depth = 0;
    let header = '';
    let sawOpenBrace = false;
    const bodyLines: string[] = [];
    let j = i;

    for (; j < rawLines.length; j += 1) {
      const l = j === i ? line : rawLines[j];
      for (const ch of l) {
        if (ch === '{') depth += 1;
        if (ch === '}') depth -= 1;
      }
      if (!sawOpenBrace) {
        const braceIdx = l.indexOf('{');
        if (braceIdx >= 0) {
          header += (j === i ? l.slice(0, braceIdx) : '');
          sawOpenBrace = true;
          const rest = l.slice(braceIdx + 1);
          if (rest.trim() && depth > 0) bodyLines.push(rest);
        } else {
          header += (header ? '\n' : '') + l;
        }
      } else if (depth > 0) {
        bodyLines.push(l);
      } else {
        // this line contains the closing brace
        const closeIdx = l.lastIndexOf('}');
        const before = l.slice(0, closeIdx);
        if (before.trim()) bodyLines.push(before);
        break;
      }

      if (sawOpenBrace && depth === 0) break;
    }

    header = header.replace(/^\w+\s*/, '').trim();
    blocks.push({ keyword, header, bodyLines, startLine, hasBraces: true });
    i = j + 1;
  }

  return blocks;
}

/** Splits a comma-separated settings string (`pk, unique, default: \`now()\``) respecting quotes/backticks and nested brackets. */
export function splitSettings(inner: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let inQuote: string | null = null;

  for (let i = 0; i < inner.length; i += 1) {
    const ch = inner[i];
    if (inQuote) {
      current += ch;
      if (ch === inQuote) inQuote = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      inQuote = ch;
      current += ch;
      continue;
    }
    if (ch === '(' || ch === '[') depth += 1;
    if (ch === ')' || ch === ']') depth -= 1;
    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());
  return parts.filter(Boolean);
}
